import { jsPDF } from "jspdf";
import autoTable, { applyPlugin } from "jspdf-autotable";
import moment from "moment";

applyPlugin(jsPDF);

// The invoice PDF (shared by the firm's invoice page and the client portal, so
// both get the same document).

const discountTypes = {
  "%": "89EDC863-9055-479B-AD08-554C0418A59F",
  $: "771B703A-5E94-4271-988A-206C0B48F7A8",
};

type PdfAddress = {
  line1?: string | null;
  city?: string | null;
  region?: string | null;
  postalCode?: string | null;
};

/** The invoice fields the PDF uses (the firm's IInvoiceView fits it). */
export type InvoicePdfData = {
  invoiceNo?: number | null;
  invoiceDate?: string | Date | null;
  dueDate?: string | Date | null;
  total?: number | null;
  discount?: number | null;
  discountTypeId?: string | null;
  isIncluded?: boolean | null;
  customer?: { name?: string | null; addresses?: PdfAddress[] | null } | null;
  invoiceCustomers?: { customers?: { name?: string | null } | null }[] | null;
  invoiceItem?:
    | {
        billingService?: { name?: string | null } | null;
        description?: string | null;
        quantity: number;
        rate: number;
        amount: number;
      }[]
    | null;
};

export type InvoicePdfFirm = {
  displayName?: string | null;
  email?: string | null;
  locations?: { locationAddress?: PdfAddress | null }[] | null;
  phoneNumbers?: { number?: string | null }[] | null;
};

export type InvoicePdfInput = {
  invoice?: InvoicePdfData;
  firm?: InvoicePdfFirm | null;
  paymentsRecieved: number;
  taxName: string;
  taxRates: number[];
  /**
   * One Summary line per tax (QA batch 17: lines can have different tax codes;
   * the firm's invoice page passes them). Without it: one combined line.
   */
  taxRows?: { label: string; amount: number }[];
  /** Amount the taxes are on ("Inclusive of Tax" note) when taxRows is given. */
  taxBase?: number;
  logoUrl?: string | null;
  notes?: string | null;
  footer?: string | null;
  fileTitle: string;
};

const sanitizeFileNamePart = (value?: string | null) =>
  (value || "Client")
    .trim()
    .replace(/\s+/g, "")
    // eslint-disable-next-line no-control-regex
    .replace(/[<>:"/\\|?*\u0000-\u001F]/g, "");

export const invoiceFileName = (clientName?: string | null) =>
  `Invoice_${sanitizeFileNamePart(clientName)}_${moment().format(
    "YYYY-MM-DD",
  )}.pdf`;

function getImageSize(url: string): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    };
    img.onerror = reject;
    img.src = url;
  });
}

const createImage = (url: string) => {
  const image = document.createElement("img");
  image.style.objectFit = "cover";
  image.style.width = "50px";
  image.style.height = "50px";
  image.src = url;
  return image;
};

const truncateText = (text: string, maxLength: number) => {
  if (text.length > maxLength) {
    return text.substring(0, maxLength) + "...";
  }
  return text;
};

export const buildInvoicePdf = async ({
  invoice,
  firm,
  paymentsRecieved,
  taxName,
  taxRates,
  taxRows,
  taxBase,
  logoUrl,
  notes,
  footer,
  fileTitle,
}: InvoicePdfInput) => {
  const doc = new jsPDF();

  doc.setProperties({
    title: fileTitle,
  });

  const getMaxWidth = () => {
    const labelInvoiceNoWidth =
      (doc.getStringUnitWidth("Invoice No.: ") * doc.getFontSize()) /
      doc.internal.scaleFactor;

    const valueInvoiceNoWidth =
      (doc.getStringUnitWidth(`${invoice?.invoiceNo || 0}`) *
        doc.getFontSize()) /
      doc.internal.scaleFactor;

    const labelInvoiceDateWidth =
      (doc.getStringUnitWidth("Invoice date: ") * doc.getFontSize()) /
      doc.internal.scaleFactor;

    const valueInvoiceDateWidth =
      (doc.getStringUnitWidth(
        `${moment(invoice?.invoiceDate).format("MMM D, YYYY")}`,
      ) *
        doc.getFontSize()) /
      doc.internal.scaleFactor;

    const labelDueDateWidth =
      (doc.getStringUnitWidth("Due date: ") * doc.getFontSize()) /
      doc.internal.scaleFactor;

    const valueDueDateWidth =
      (doc.getStringUnitWidth(
        `${moment(invoice?.dueDate).format("MMM D, YYYY")}`,
      ) *
        doc.getFontSize()) /
      doc.internal.scaleFactor;

    return Math.max(
      labelInvoiceNoWidth + valueInvoiceNoWidth,
      labelInvoiceDateWidth + valueInvoiceDateWidth,
      labelDueDateWidth + valueDueDateWidth,
    );
  };

  const renderBoldLabel = (
    label: string,
    value: string,
    y: number,
    isBoldBoth?: boolean,
    middleValue?: string,
  ) => {
    doc.setFont("helvetica", "bold");
    const labelWidth =
      (doc.getStringUnitWidth(label + ": ") * doc.getFontSize()) /
      doc.internal.scaleFactor;

    doc.text(label + ":", 150, y);

    let valueWidth = 0;
    if (value) {
      doc.setFont("helvetica", isBoldBoth ? "bold" : "normal");
      valueWidth =
        (doc.getStringUnitWidth(value) * doc.getFontSize()) /
        doc.internal.scaleFactor;

      const startX = doc.internal.pageSize.width - 10 - valueWidth;
      doc.text(value, startX, y);
    }

    if (middleValue) {
      doc.setFontSize(9);
      const middleValueWidth =
        (doc.getStringUnitWidth(middleValue) * doc.getFontSize()) /
        doc.internal.scaleFactor;

      const middleValueX = 10 + labelWidth;
      doc.setFont("helvetica", "normal");
      doc.text(middleValue || "", middleValueX, y);
      doc.setFontSize(10);
    }
  };
  const renderLeftAlignedLabel = (label: string, value: string, y: number) => {
    doc.setFont("helvetica", "bold");

    const startX = 150;

    // Print the label at the left position
    doc.text(label + ":", startX, y);

    if (value) {
      doc.setFont("helvetica", "normal");

      // Print the value next to the label
      const valueX = 180;
      doc.text(value, valueX, y);
    }
  };
  doc.setFontSize(10);
  doc.setFont("helvetica", "", "normal");

  const subtotal = () => {
    let value = 0;
    invoice?.invoiceItem?.forEach((item) => (value += item.amount));
    return value;
  };

  const discountValue = () => {
    if (invoice?.discountTypeId === discountTypes.$.toLowerCase()) {
      return invoice?.discount || 0;
    }
    if (invoice?.discountTypeId === discountTypes["%"].toLowerCase()) {
      return (subtotal() * (invoice?.discount || 0)) / 100;
    }
    return 0;
  };

  const taxValue = () => {
    const taxRatesSum = Array.isArray(taxRates)
      ? taxRates.reduce((sum, item) => sum + (item || 0), 0)
      : 0;

    if (invoice?.isIncluded) {
      const tax = taxRatesSum / 100 + 1;

      const subtotalWithoutTax =
        invoice?.invoiceItem?.reduce(
          (acc, item) => acc + item.amount / tax,
          0,
        ) || 0;

      return (
        ((subtotalWithoutTax - (discountValue() || 0)) * taxRatesSum) / 100
      );
    }

    return (((subtotal() || 0) - (discountValue() || 0)) * taxRatesSum) / 100;
  };

  const rightAlignText = ({ text, y }: { text: string; y: number }) => {
    const textWidth =
      (doc.getStringUnitWidth(text) * doc.getFontSize()) /
      doc.internal.scaleFactor;
    const textOffset = doc.internal.pageSize.width - 10 - textWidth;
    doc.text(text, textOffset, y);
  };

  const checkAndAddPage = (y: number) => {
    if (y > doc.internal.pageSize.height - 20) {
      doc.addPage();
      return 10;
    }
    return y;
  };

  const maxImageHeight = 20; // Maximum image height
  const maxImageWidth = 60; // Maximum image width
  let imgWidth = 0;
  let imgHeight = 0;

  if (logoUrl) {
    const size = await getImageSize(logoUrl);
    const imageRatio = size.width / size.height;

    imgHeight = size.height > maxImageHeight ? maxImageHeight : size.height;
    imgWidth = imgHeight * imageRatio;

    if (imgWidth > maxImageWidth) {
      imgWidth = maxImageWidth;
      imgHeight = imgWidth / imageRatio;
    }
  }

  // Add company information
  let y = 10;

  if (logoUrl && imgWidth && imgHeight) {
    const imageData = createImage(logoUrl);
    doc.addImage(imageData, "JPEG", 10, y, imgWidth, imgHeight);
  }

  doc.setFontSize(18).setFont("helvetica", "", 700);
  rightAlignText({ text: "INVOICE", y });
  y += 10;

  const companyDetails = [
    `${truncateText(firm?.displayName || "", 50)}`,
    `${truncateText(firm?.locations?.[0]?.locationAddress?.line1 || "", 50)}`,
    `${truncateText(
      `${firm?.locations?.[0]?.locationAddress?.city}, ${firm?.locations?.[0]?.locationAddress?.region} ${firm?.locations?.[0]?.locationAddress?.postalCode}`,
      150,
    )}`,
    `${truncateText(firm?.phoneNumbers?.[0]?.number || "", 50)}`,
    `${truncateText(firm?.email || "", 50)}`,
  ];
  companyDetails.forEach((detail, index) => {
    if (detail) {
      if (index === 0) {
        doc.setFontSize(13).setFont("helvetica", "", "bold");
        rightAlignText({
          text: detail,
          y,
        });
      } else {
        doc.setFontSize(10).setFont("helvetica", "", "normal");
        rightAlignText({
          text: detail,
          y,
        });
      }
      rightAlignText({
        text: detail,
        y,
      });
      y += 5;
    }
  });

  if (imgHeight > 50) {
    y += imgHeight + 15 - y;
  } else {
    y += 5;
  }

  doc.setDrawColor(190, 190, 190);
  const pageWidth = doc.internal.pageSize.width;
  doc.line(0, y, pageWidth, y);
  y += 10;
  // Add customer details
  let leftY = y;
  let rightY = y;

  y = checkAndAddPage(y);
  doc
    .setFontSize(10)
    .setFont("helvetica", "", "normal")
    .setTextColor("#5E5D5D");
  doc.text("BILL TO", 10, y).setTextColor(0);
  y += 5;
  leftY += 5;
  rightY += 5;
  doc.setFontSize(10).setFont("helvetica", "", "bold");
  doc.text(`${invoice?.customer?.name || ""}`, 10, leftY);
  doc.setFontSize(10).setFont("helvetica", "bold");

  leftY += 5;
  y += 5;

  rightY += 5;
  y += 5;
  renderLeftAlignedLabel("Invoice No.", `${invoice?.invoiceNo || 0}`, rightY);

  rightY += 5;
  y += 5;
  renderLeftAlignedLabel(
    "Invoice date",
    `${moment(invoice?.invoiceDate).format("MMM D, YYYY")}`,
    rightY,
  );

  rightY += 5;
  renderLeftAlignedLabel(
    "Due Date",
    `${moment(invoice?.dueDate).format("MMM D, YYYY")}`,
    rightY,
  );

  doc.setFont("helvetica", "bold");
  invoice?.invoiceCustomers?.forEach((item) => {
    leftY = checkAndAddPage(leftY);
    doc.text(item?.customers?.name || "", 10, leftY);
    leftY += 5;
    y += 5;
  });
  doc.setFontSize(10).setFont("helvetica", "", "normal");
  doc.text(`${invoice?.customer?.addresses?.[0]?.line1 || ""}`, 10, leftY);
  leftY += 5;
  y += 5;
  doc.setFont("helvetica", "normal");
  doc.text(
    `${invoice?.customer?.addresses?.[0]?.city || ""} ${
      invoice?.customer?.addresses?.[0]?.region || ""
    } ${invoice?.customer?.addresses?.[0]?.postalCode || ""}`,
    10,
    leftY,
  );

  leftY += 10;
  y += 10;

  // Add table rows
  y = doc.lastAutoTable.finalY || y - 20;
  const tableData =
    invoice?.invoiceItem?.map((item) => [
      item?.billingService?.name || "",
      item?.description || "",
      item?.quantity.toString() || "",
      `$${(item?.rate || 0).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`,
      `$${(item?.amount || 0).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`,
    ]) || [];

  const tableColumns = ["Service Name", "Description", "Qty", "Rate", "Amount"];
  y = checkAndAddPage(y + 20);
  doc.setTextColor(0);
  autoTable(doc, {
    startY: y,
    head: [tableColumns],
    margin: { left: 0, bottom: 0 },
    body: tableData,
    theme: "grid",
    tableWidth: doc.internal.pageSize.width,
    styles: {
      cellWidth: "auto",
      textColor: [0, 0, 0],
    },
    headStyles: {
      fillColor: [85, 85, 85],
      textColor: [255, 255, 255],
      cellPadding: { left: 10, vertical: 2.5 },
    },
    columnStyles: {
      0: {
        cellWidth: "auto",
        minCellWidth: 40,
        lineWidth: 0,
        fontStyle: "bold",
        font: "helvetica",
        cellPadding: { left: 10, vertical: 1.5 },
        halign: "left",
      },
      1: {
        cellWidth: "auto",
        lineWidth: 0,
        font: "helvetica",
        cellPadding: { left: 10, top: 1.5, right: 2 },
        halign: "left",
      },
      2: {
        cellWidth: "auto",
        minCellWidth: 20,
        lineWidth: 0,
        font: "helvetica",
        cellPadding: { left: 10, top: 1.5 },
        halign: "left",
      },
      3: {
        cellWidth: "auto",
        minCellWidth: 30,
        lineWidth: 0,
        font: "helvetica",
        cellPadding: { left: 10, top: 1.5, right: 2 },
        halign: "left",
      },
      4: {
        cellWidth: "auto",
        minCellWidth: 35,
        lineWidth: 0,
        font: "helvetica",
        cellPadding: { left: 10, top: 1.5, right: 2 },
        halign: "left",
      },
    },
  });
  y = checkAndAddPage(doc.lastAutoTable.finalY);
  doc.line(0, y, doc.internal.pageSize.width, y);
  y = checkAndAddPage(y + 20);

  renderBoldLabel(
    "Subtotal",
    `$${(subtotal() || 0).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`,
    y,
  );
  y = checkAndAddPage(y + 5);
  if (discountValue()) {
    renderBoldLabel(
      "Discount",
      `$${(discountValue() || 0).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`,
      y,
    );
    y = checkAndAddPage(y + 5);
  }

  const pdfMoney = (value: number) =>
    `$${(value || 0).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  if (taxRows?.length) {
    // QA batch 17: each tax on its own line (lines may have different codes).
    taxRows.forEach((row, index) => {
      const isLast = index === taxRows.length - 1;
      renderBoldLabel(
        row.label,
        pdfMoney(row.amount),
        y,
        false,
        invoice?.isIncluded && isLast && taxBase != null
          ? `(Included, on ${pdfMoney(taxBase)}) `
          : "",
      );
      y = checkAndAddPage(y + 5);
    });
  } else {
    // DATA-001 (QA batch 17): the saved total wins when today's rates don't
    // give it (rates changed after saving): show the tax as saved.
    const computedTax = taxValue() || 0;
    const afterDiscount = Math.max(subtotal() - discountValue(), 0);
    const savedTotal = invoice?.total;
    const shownTax =
      !invoice?.isIncluded &&
      savedTotal != null &&
      Math.abs(afterDiscount + computedTax - savedTotal) > 0.01
        ? savedTotal - afterDiscount
        : computedTax;

    const includesOn = pdfMoney(subtotal() - computedTax - discountValue());

    renderBoldLabel(
      `${taxName}`,
      pdfMoney(shownTax),
      y,
      false,
      invoice?.isIncluded ? `(Includes ${taxName} on ${includesOn}) ` : "",
    );
    y = checkAndAddPage(y + 5);
  }

  // Draw line after tax
  doc.line(
    ((doc.internal.pageSize.width - 10) / 4) * (invoice?.isIncluded ? 2.5 : 3),
    y,
    doc.internal.pageSize.width - 10,
    y,
  );
  y = checkAndAddPage(y + 8);

  renderBoldLabel(
    "Total",
    `$${(invoice?.total || 0).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`,
    y,
  );
  y = checkAndAddPage(y + 5);

  // Draw line after total
  doc.line(
    ((doc.internal.pageSize.width - 10) / 4) * (invoice?.isIncluded ? 2.5 : 3),
    y,
    doc.internal.pageSize.width - 10,
    y,
  );
  y = checkAndAddPage(y + 8);
  if (paymentsRecieved) {
    renderBoldLabel(
      "Payment received",
      `$${(paymentsRecieved || 0).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`,
      y,
    );
    y = checkAndAddPage(y + 10);
  }
  renderBoldLabel(
    "Balance due",
    `$${((invoice?.total || 0) - (paymentsRecieved || 0)).toLocaleString(
      "en-US",
      {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      },
    )}`,
    y,
    true,
  );
  const pageHeight = doc.internal.pageSize.height;

  const subtotalPositionY = pageHeight - 122;

  const notesText = notes || "";

  const maxWidth = doc.internal.pageSize.width / 3;

  doc.setFont("helvetica", "normal");

  const notesX = 10;

  doc.text(notesText, notesX, subtotalPositionY, { maxWidth: maxWidth });

  const footerText = footer || "";

  const footerY = pageHeight - 10;

  const footerWidth =
    (doc.getStringUnitWidth(footerText) * doc.getFontSize()) /
    doc.internal.scaleFactor;
  const footerX = (doc.internal.pageSize.width - footerWidth) / 2;

  doc.setFont("helvetica", "normal");
  doc.text(footerText, footerX, footerY);

  return doc;
};
